import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  CalendarPlus,
  Check,
  Copy,
  Pencil,
  RotateCcw,
  Repeat,
  Settings,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type {
  RegistrationStatus,
  TrainingEventCard,
  TrainingEventCommentDto,
  TrainingGroupResponse,
  TrainingParticipantsView,
  TrainingTrainerOption,
} from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Field, Input } from '../components/ui';
import { DateBadge } from '../components/DateBadge';
import { GroupFilterDropdown, WeekdayFilterDropdown } from '../components/FilterDropdowns';
import { Modal } from '../components/Modal';
import type { RosterGroup } from '../components/RosterModal';
import { useClickOutside } from '../lib/use-click-outside';
import { useLockBodyScroll } from '../lib/use-lock-body-scroll';
import { isoWeekday, WD } from '../lib/weekday';
import { buildTrainingEventPath, buildTrainingEventSlug } from '../lib/training-event-slug';

/** Visual grouping for the Termin-/Serien-Formulare inside `AddEventModal`
 * and `ManageSeriesModal` — a soft card per logical group (Was/Wann/Wo)
 * instead of one long flat field list. */
function FormSection({ label, children }: { label: string; children: ReactNode }) {
  // § Nutzerfeedback „Felder insgesamt ein wenig kleiner" — etwas knapperes
  // Padding/Gap als zuvor (`p-3.5`/`gap-3` → `p-3`/`gap-2.5`); die Feld-
  // schrift selbst bleibt unangetastet (auf Mobile ohnehin fest auf 16px
  // erzwungen, siehe index.css' iOS-Zoom-Regel — kleiner als das würde den
  // Auto-Zoom-Bug zurückbringen).
  return (
    <div className="space-y-2.5 rounded-xl bg-slate-50 p-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
      <div className="grid grid-cols-2 gap-2.5">{children}</div>
    </div>
  );
}

/** GET /training/series returns the training_series row as-is — no dedicated
 * contracts type exists yet, so this mirrors packages/db's TrainingSeries
 * shape for the fields the admin table/edit-form actually needs. */
interface TrainingSeriesRow {
  id: string;
  title: string;
  trainingGroupIds: string[];
  trainerMemberIds: string[];
  trainerPossible: boolean;
  activeTrainingOffered: boolean;
  venue: string | null;
  hall: string | null;
  weekdays: number[];
  startDate: string;
  endDate: string | null;
  startTime: string;
  endTime: string;
  status: 'active' | 'ended' | 'cancelled';
}

/** GET /training/events/:id returns the training_events row as-is (plus
 * trainingGroupIds/trainerMemberIds, assembled server-side from their join
 * tables same as TrainingSeriesRow above) — no dedicated contracts type,
 * same reasoning. Used only to pre-fill the "einzelnen Termin bearbeiten"
 * form (§ Nutzeranfrage), not for the card itself (that's TrainingEventCard). */
export interface TrainingEventRow {
  id: string;
  seriesId: string | null;
  title: string;
  trainingGroupIds: string[];
  trainerMemberIds: string[];
  trainerPossible: boolean;
  activeTrainingOffered: boolean;
  venue: string | null;
  hall: string | null;
  eventDate: string;
  startTime: string;
  endTime: string;
  maxParticipants: number | null;
  warningText: string | null;
  warningColor: string | null;
}

/** Preset-Palette für die Warnbanner-Hintergrundfarbe (§ Nutzeranfrage
 * „leichte Hintergrundfarbe auswählen") — bewusst eine feste, helle
 * Auswahl statt eines freien Colorpickers, damit der Text auf jeder davon
 * gut lesbar bleibt (dieselben Pastelltöne wie die bestehenden
 * Status-Kapseln, z. B. `bg-amber-100`/`bg-red-100`). */
const WARNING_COLOR_PRESETS: Array<{ label: string; hex: string }> = [
  { label: 'Gelb', hex: '#fef3c7' },
  { label: 'Rot', hex: '#fee2e2' },
  { label: 'Blau', hex: '#dbeafe' },
  { label: 'Grün', hex: '#dcfce7' },
  { label: 'Lila', hex: '#f3e8ff' },
  { label: 'Grau', hex: '#f1f5f9' },
];

/** Open discussion thread on one training event — read for anyone with
 * training.view, post/delete-own for anyone with training.respond, moderate
 * (delete any) for training.manage. Distinct from the private per-response
 * note captured on "Anmelden"/"Abmelden" in the roster above it. Lives as a
 * section inside `TrainingMenuModal` (no own backdrop/header — the merged
 * dialog's "umsetzen"-Entwurf hat kein eigenes Chat-Icon/Fenster mehr, siehe
 * docs/training.md). */
function CommentsSection({ eventId, canRespond }: { eventId: string; canRespond: boolean }) {
  const qc = useQueryClient();
  const queryKey = ['training', 'comments', eventId];
  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => api<TrainingEventCommentDto[]>(`/training/events/${eventId}/comments`, { orgScoped: true }),
  });

  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const post = useMutation({
    mutationFn: () =>
      api<TrainingEventCommentDto>(`/training/events/${eventId}/comments`, {
        method: 'POST',
        orgScoped: true,
        body: { comment: text },
      }),
    onSuccess: () => {
      setText('');
      setError(null);
      void qc.invalidateQueries({ queryKey });
      // the comment-count badge on the card lives in the events list, not this thread
      void qc.invalidateQueries({ queryKey: ['training', 'events'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Kommentar konnte nicht gespeichert werden.'),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/training/events/${eventId}/comments/${id}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey });
      void qc.invalidateQueries({ queryKey: ['training', 'events'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Kommentar konnte nicht gelöscht werden.'),
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col md:min-h-[200px]">
      <div className="min-h-0 flex-1 overflow-y-auto md:max-h-[55vh]">
        {isLoading && <p className="text-sm text-slate-400">Lädt …</p>}
        {!isLoading && data?.length === 0 && (
          <p className="text-sm text-slate-400">Noch keine Kommentare zu diesem Training.</p>
        )}
        {data && data.length > 0 && (
          <ul className="space-y-2">
            {data.map((c) => (
              <li key={c.id} className="rounded-md bg-slate-50 p-2.5 text-sm">
                <div className="mb-0.5 flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-700">{c.authorName}</span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-slate-400">
                    {new Date(c.createdAt).toLocaleString('de-DE', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                    {c.canDelete && (
                      <button
                        type="button"
                        title="Kommentar löschen"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (confirm('Kommentar wirklich löschen?')) remove.mutate(c.id);
                        }}
                        className="text-slate-400 hover:text-red-600"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-slate-700">{c.comment}</p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error && (
        <div className="mt-2 shrink-0">
          <Alert>{error}</Alert>
        </div>
      )}

      {canRespond && (
        <form
          className="mt-3 flex shrink-0 gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) post.mutate();
          }}
        >
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Kommentar schreiben …"
            rows={2}
            maxLength={2000}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
          />
          <Button type="submit" disabled={!text.trim() || post.isPending} className="self-end">
            {post.isPending ? 'Sendet…' : 'Senden'}
          </Button>
        </form>
      )}
    </div>
  );
}

/** Kombiniertes Trainings-Menü (§ Nutzeranfrage "umsetzen") — ersetzt die
 * frühere Aufteilung in ein separates Anmeldungs-Fenster (`RosterModal`) und
 * ein separates Kommentar-Fenster: ein Klick auf das Personen-Icon der Karte
 * öffnet beides zusammen. Mobil gestapelt (erst Rückmeldung/Teilnehmer, dann
 * Kommentare), ab `md` zweispaltig nebeneinander (links Rückmeldung, rechts
 * Kommentare) — siehe docs/training.md. */
/** Erweiterung von `RosterGroup.items` nur für die Trainingsseite (§
 * Nutzeranfrage „grüne Kapsel … Training"): `RosterModal.tsx`s `RosterGroup`
 * bleibt unverändert, da sie sich die Season-Seiten (`SeasonPage`/
 * `CalendarPage`) teilen, für die dieses Feld nicht existiert. */
type TrainingRosterGroup = Omit<RosterGroup, 'items'> & {
  items: Array<RosterGroup['items'][number] & { wantsActiveTraining?: boolean }>;
};

export function TrainingMenuModal({
  title,
  subtitle,
  eventId,
  respondableMembers,
  activeTrainingOffered,
  onRespond,
  respondPending,
  canRespond,
  rosterGroups,
  onReset,
  resettingId,
  onEdit,
  onDelete,
  deletePending,
  onClose,
  shareUrl,
}: {
  title: string;
  subtitle: string;
  eventId: string;
  respondableMembers: Array<{
    memberId: string;
    name: string;
    status: RegistrationStatus;
    isOwn: boolean;
    wantsActiveTraining: boolean;
  }>;
  /** § Nutzeranfrage: ob auf diesem Termin die Checkbox "Training" überhaupt
   * angeboten wird — admin-konfigurierbar je Serie/Einzeltermin. */
  activeTrainingOffered: boolean;
  onRespond: (memberId: string, status: 'registered' | 'declined', wantsActiveTraining?: boolean) => void;
  respondPending: boolean;
  canRespond: boolean;
  rosterGroups: TrainingRosterGroup[];
  onReset?: (id: string) => void;
  resettingId?: string | null;
  onEdit?: () => void;
  onDelete?: () => void;
  deletePending?: boolean;
  onClose: () => void;
  /** Absolute URL zur eigenen Seite dieses Trainings (§ Nutzeranfrage „jedes
   * Training eine separate Seite/ID … auch als Pop-up anzeigen" —
   * `/training/events/$slug`, siehe `training-event-slug.ts`). Optional,
   * damit kein Aufrufer gezwungen ist, sie zu bauen; wenn gesetzt, zeigt die
   * Kopfzeile einen Kopieren-Button dafür, egal ob dieses Menü gerade über
   * einen Klick (lokaler State) oder direkt über diese Seite geöffnet wurde. */
  shareUrl?: string;
}) {
  useLockBodyScroll();
  const [copied, setCopied] = useState(false);

  return (
    <div
      className="modal-backdrop-in fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="modal-panel-in flex max-h-[85vh] w-full max-w-sm flex-col overflow-hidden rounded-xl bg-white p-4 shadow-xl md:max-w-3xl md:p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-1 flex shrink-0 items-center justify-between gap-2">
          <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
            <Users size={18} />
            {title}
          </h2>
          <div className="flex items-center gap-1">
            {shareUrl && (
              <button
                type="button"
                title="Link zu diesem Training kopieren"
                aria-label="Link zu diesem Training kopieren"
                onClick={() => {
                  void navigator.clipboard.writeText(`${window.location.origin}${shareUrl}`);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              </button>
            )}
            {/* § Nutzeranfrage: Bearbeiten-Button links neben dem Mülleimer —
                bearbeitet nur diesen einzelnen Termin (siehe EventFormFields'
                `editing`-Hinweis bei Serienterminen). */}
            {onEdit && (
              <button
                type="button"
                title="Termin bearbeiten"
                aria-label="Termin bearbeiten"
                onClick={onEdit}
                className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <Pencil size={16} />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                title="Termin löschen"
                aria-label="Termin löschen"
                disabled={deletePending}
                onClick={onDelete}
                className="rounded-md p-1 text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
              >
                <Trash2 size={17} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <p className="mb-3 shrink-0 text-xs text-slate-500">{subtitle}</p>

        <div className="min-h-0 flex-1 overflow-y-auto md:flex md:gap-5 md:overflow-visible">
          <div className="md:min-h-0 md:flex-1 md:overflow-y-auto">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
              Rückmeldung &amp; Teilnehmer
            </div>

            {respondableMembers.length > 0 && (
              <div className="mb-3 space-y-2 rounded-lg bg-blue-50 p-2.5">
                {respondableMembers.map((m) => {
                  const isRegistered = m.status === 'registered' || m.status === 'waitlist';
                  const isDeclined = m.status === 'declined';
                  return (
                    <div key={m.memberId} className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-700">{m.isOwn ? 'Ich' : m.name}</span>
                      <div className="flex shrink-0 items-center gap-2">
                        {/* § Nutzeranfrage: zusätzliche Option, bei der Anmeldung auch
                            aktives Training zu markieren — nur auf dafür freigegebenen
                            Terminen (`activeTrainingOffered`), als runde Kapsel (§
                            Nutzeranfrage „runder Kasten mit der Schrift Training") statt
                            einer schlichten Checkbox. Antippen meldet (bei Bedarf) zugleich
                            an, erneutes Antippen nimmt nur den Haken zurück. */}
                        {activeTrainingOffered && (
                          <button
                            type="button"
                            disabled={respondPending}
                            aria-pressed={m.wantsActiveTraining}
                            onClick={() => onRespond(m.memberId, 'registered', !m.wantsActiveTraining)}
                            className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold transition disabled:opacity-60 ${
                              m.wantsActiveTraining
                                ? 'bg-green-600 text-white shadow-sm shadow-green-600/30'
                                : 'border border-slate-200 bg-white text-slate-400 hover:bg-green-50 hover:text-green-600'
                            }`}
                          >
                            {m.wantsActiveTraining && <Check size={12} />}
                            Training
                          </button>
                        )}
                        {/* § Nutzeranfrage „moderner … Daumen hoch und Daumen
                            runter, kein Anmelden/Abmelden-Text": zwei runde
                            Icon-Buttons statt der zweigeteilten Text-Pille. */}
                        <div className="flex shrink-0 items-center gap-1.5">
                          <button
                            type="button"
                            title="Anmelden"
                            aria-label="Anmelden"
                            disabled={respondPending}
                            onClick={() => onRespond(m.memberId, 'registered', m.wantsActiveTraining)}
                            className={`flex h-8 w-8 items-center justify-center rounded-full transition disabled:opacity-60 ${
                              isRegistered
                                ? 'bg-green-600 text-white shadow-sm shadow-green-600/30'
                                : 'bg-white text-slate-400 hover:bg-green-50 hover:text-green-600'
                            }`}
                          >
                            <ThumbsUp size={15} fill={isRegistered ? 'currentColor' : 'none'} />
                          </button>
                          <button
                            type="button"
                            title="Abmelden"
                            aria-label="Abmelden"
                            disabled={respondPending}
                            onClick={() => onRespond(m.memberId, 'declined')}
                            className={`flex h-8 w-8 items-center justify-center rounded-full transition disabled:opacity-60 ${
                              isDeclined
                                ? 'bg-red-600 text-white shadow-sm shadow-red-600/30'
                                : 'bg-white text-slate-400 hover:bg-red-50 hover:text-red-600'
                            }`}
                          >
                            <ThumbsDown size={15} fill={isDeclined ? 'currentColor' : 'none'} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {rosterGroups.map((g) => (
              <div key={g.key} className="mb-3 last:mb-0">
                <div className="mb-1.5 flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <span>{g.label}</span>
                  <span className={g.countClass}>{g.items.length}</span>
                </div>
                {g.items.length === 0 ? (
                  <p className="py-1 text-xs text-slate-400">—</p>
                ) : (
                  <ul className="space-y-1">
                    {g.items.map((item) => (
                      <li
                        key={item.id}
                        className={`flex items-center justify-between rounded-md px-2 py-1.5 text-sm ${
                          item.isTrainer && g.trainerRowClass ? g.trainerRowClass : g.rowClass
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          {g.icon}
                          {item.name}
                          {item.hint && <span className="text-xs font-normal text-slate-400">({item.hint})</span>}
                          {/* § Nutzeranfrage: zusätzliche grüne Kapsel für Mitglieder, die
                              bei der Anmeldung "Training" angehakt haben — der bestehende
                              grüne/rote/gelbe Zeilenhintergrund (`rowClass`) bleibt dafür
                              unverändert. */}
                          {item.wantsActiveTraining && (
                            <span className="rounded-full bg-green-600 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-white">
                              Training
                            </span>
                          )}
                        </span>
                        {onReset && (
                          <button
                            type="button"
                            title="Zurücksetzen"
                            onClick={() => onReset(item.id)}
                            disabled={resettingId === item.id}
                            className="rounded p-1 text-slate-400 hover:bg-white/70 hover:text-slate-600 disabled:opacity-40"
                          >
                            <RotateCcw size={14} />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>

          <div className="my-3 shrink-0 border-t border-slate-100 md:my-0 md:w-px md:border-l md:border-t-0" />

          <div className="md:min-h-0 md:flex-1">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Kommentare</div>
            <CommentsSection eventId={eventId} canRespond={canRespond} />
          </div>
        </div>
      </div>
    </div>
  );
}

function EventCard({ e }: { e: TrainingEventCard }) {
  const { can } = useAuth();
  const navigate = useNavigate();

  // § Nutzerfeedback „in der Adresszeile bleibt nur /training … den
  // entsprechenden Link haben" — das Trainings-Menü lebte bis dahin als
  // reiner Client-State direkt in dieser Karte (eigene `respond`/`reset`/
  // `removeEvent`-Mutationen, eigenes Rückmeldung-Formular). Ein Klick
  // navigiert jetzt stattdessen echt zur eigenen Seite dieses Trainings
  // (`TrainingEventPage.tsx`, Route `/training/events/$slug`) — dieselbe
  // `TrainingRespondModal`/`TrainingMenuModal`-Kombination wie dort, jetzt
  // aber um `onEdit`/`onDelete` ergänzt (s. dort), damit beim Navigieren
  // hierher keine Funktion verloren geht. Löst die zuvor hier dupliziert
  // geführte Rückmeldungs-/Teilnehmer-/Lösch-Logik komplett ab, statt sie
  // zusätzlich zur Seite zu pflegen.

  // Farbe des Personen-Icons zeigt die eigene Rückmeldung — "eigen" ist hier
  // das erste `isOwn`-Mitglied (bei verknüpften Kindern kann es mehrere
  // respondable members geben, aber nur eines ist man selbst). § Nutzeranfrage
  // „angemeldet grün, abgemeldet rot, noch nicht abgestimmt grau".
  const ownStatus = e.respondableMembers.find((m) => m.isOwn)?.status;
  const ownRegistered = ownStatus === 'registered' || ownStatus === 'waitlist';
  const ownDeclined = ownStatus === 'declined';
  const menuButtonClass = ownRegistered
    ? 'bg-green-50 text-green-600'
    : ownDeclined
      ? 'bg-red-50 text-red-600'
      : 'bg-slate-100 text-slate-500';

  return (
    <div className="relative min-w-0 rounded-xl border border-slate-200 bg-white px-4 py-2.5 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <DateBadge date={e.eventDate} />
          <div className="w-1 shrink-0 self-stretch rounded-full bg-brand-accent" />
          <div className="min-w-0 flex-1">
            {e.status !== 'scheduled' && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="rounded bg-red-50 px-1.5 py-0.5 font-medium text-red-600">
                  {e.status === 'cancelled' ? 'Abgesagt' : e.status === 'moved' ? 'Verlegt' : 'Abgeschlossen'}
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="truncate text-sm font-semibold">{e.title}</span>
              {/* § Nutzeranfrage: Trainingsgruppen-Kapsel(n) hinter dem Titel
                  kleiner als zuvor. */}
              {e.trainingGroupNames.map((name) => (
                <span
                  key={name}
                  className="shrink-0 rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-medium text-slate-500"
                >
                  {name}
                </span>
              ))}
            </div>
            {(e.venue ?? e.hall) && (
              <div className="truncate text-xs text-slate-500">
                {e.venue}
                {e.venue && e.hall ? ` · ${e.hall}` : (e.hall ?? '')}
              </div>
            )}
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
              <span>
                {e.startTime.slice(0, 5)}–{e.endTime.slice(0, 5)}
              </span>
              {e.registeredCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700">
                  ✓ {e.registeredCount}
                </span>
              )}
              {e.maxParticipants != null && <span className="text-slate-400">/ {e.maxParticipants}</span>}
              {e.waitlistCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
                  ? {e.waitlistCount}
                </span>
              )}
              {e.declinedCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-semibold text-red-700">
                  ✕ {e.declinedCount}
                </span>
              )}
            </div>
            {/* § Nutzeranfrage: Warnbanner unter Uhrzeit/Sporthalle, nur
                sichtbar wenn im Bearbeitungsformular ein Hinweistext gesetzt
                wurde — Hintergrundfarbe dort frei aus einer Preset-Palette
                gewählt. */}
            {e.warningText && (
              <div
                className="mt-1.5 rounded-md px-2 py-1 text-xs text-slate-700"
                style={{ backgroundColor: e.warningColor ?? WARNING_COLOR_PRESETS[0]!.hex }}
              >
                {e.warningText}
              </div>
            )}
          </div>
        </div>

        {/* § Nutzeranfrage "umsetzen": ein einzelnes Personen-Icon ersetzt die
            frühere An-/Abmelde-Button-Spalte — öffnet das kombinierte
            Trainings-Menü (Rückmeldung+Teilnehmer und Kommentare). Icon-Farbe
            zeigt die eigene Rückmeldung (grün/rot/grau, § Nutzeranfrage),
            blaue Zahl = Kommentaranzahl — beide schon auf der Karte sichtbar,
            ohne das Menü zu öffnen (siehe docs/training.md). */}
        {can('training.view') && (
          <button
            type="button"
            title="Trainings-Menü öffnen"
            aria-label={`Trainings-Menü öffnen${ownRegistered ? ' — du bist angemeldet' : ownDeclined ? ' — du bist abgemeldet' : ''}${
              e.commentCount > 0 ? `, ${e.commentCount} Kommentar${e.commentCount === 1 ? '' : 'e'}` : ''
            }`}
            onClick={() => void navigate({ to: '/training/events/$slug', params: { slug: buildTrainingEventSlug(e) } })}
            className={`relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${menuButtonClass}`}
          >
            <Users size={20} />
            {e.commentCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full border-2 border-white bg-brand-accent px-1 text-[10px] font-bold leading-none text-white"
              >
                {e.commentCount}
              </span>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/** Self-contained Variante von `TrainingMenuModal` für Aufrufer, die nicht
 * ohnehin schon die passenden Daten/Mutationen zur Hand haben — holt sich
 * Karte+Teilnehmer selbst über die `id` und reicht alles an
 * `TrainingMenuModal` durch. Zwei Aufrufer: `CalendarPage.tsx`s
 * Klick-to-Respond-Pop-up (§ Nutzeranfrage, siehe docs/calendar.md, **ohne**
 * `onEdit`/`onDelete` — Vereinsverwaltung passiert dort bewusst nicht
 * inline) und `TrainingEventPage.tsx`, die eigene Seite eines Trainings
 * (§ Nutzeranfrage „jedes Training eine separate Seite/ID … auch als
 * Pop-up anzeigen", mittlerweile der **einzige** Aufrufer von
 * `TrainingMenuModal` überhaupt — `EventCard` auf der Trainingsseite selbst
 * navigiert seit § Nutzerfeedback „Adresszeile … den entsprechenden Link
 * haben" ebenfalls hierher, statt ein eigenes, dupliziertes Menü lokal zu
 * halten, siehe docs/training.md). */
export function TrainingRespondModal({
  eventId,
  onClose,
  onChanged,
  onEdit,
  onDelete,
  deletePending,
}: {
  eventId: string;
  onClose: () => void;
  /** Zusätzlich zur eigenen Invalidierung aufgerufen — z. B. `CalendarPage.tsx`s
   * Kalender-Query, damit die Grün/Rot-Einfärbung nach einer Rückmeldung
   * sofort reagiert (siehe docs/calendar.md). */
  onChanged?: () => void;
  /** Nur von `TrainingEventPage.tsx` gesetzt (s. o.) — dort lebt die
   * Bearbeiten-/Lösch-Logik inkl. `EditEventModal`, hier nur durchgereicht. */
  onEdit?: () => void;
  onDelete?: () => void;
  deletePending?: boolean;
}) {
  const { can } = useAuth();
  const qc = useQueryClient();

  const { data: card } = useQuery({
    queryKey: ['training', 'card', eventId],
    queryFn: () => api<TrainingEventCard>(`/training/events/${eventId}/card`, { orgScoped: true }),
  });
  const { data: parts } = useQuery({
    queryKey: ['training', 'participants', eventId],
    queryFn: () => api<TrainingParticipantsView>(`/training/events/${eventId}/participants`, { orgScoped: true }),
  });

  const invalidateLocal = () => {
    void qc.invalidateQueries({ queryKey: ['training', 'card', eventId] });
    void qc.invalidateQueries({ queryKey: ['training', 'participants', eventId] });
    onChanged?.();
  };

  const respond = useMutation({
    mutationFn: (vars: { memberId: string; status: 'registered' | 'declined'; wantsActiveTraining?: boolean }) =>
      api(`/training/events/${eventId}/respond`, {
        method: 'POST',
        orgScoped: true,
        body: { status: vars.status, memberId: vars.memberId, wantsActiveTraining: vars.wantsActiveTraining },
      }),
    onSuccess: invalidateLocal,
  });

  const reset = useMutation({
    mutationFn: (memberId: string) =>
      api(`/training/events/${eventId}/participants/${memberId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: invalidateLocal,
  });

  if (!card) return null;

  const registered = parts?.lists?.registered ?? [];
  const waitlist = parts?.lists?.waitlist ?? [];
  const declined = parts?.lists?.declined ?? [];
  const guardianHint = (p: { respondedByGuardian: boolean }) =>
    p.respondedByGuardian ? 'durch Elternteil' : undefined;

  const rosterGroups: TrainingRosterGroup[] = [
    {
      key: 'registered',
      label: 'Angemeldet',
      icon: <span className="text-green-600">✓</span>,
      countClass: 'text-green-600',
      rowClass: 'bg-green-50',
      trainerRowClass: 'bg-green-200',
      items: registered.map((p) => ({
        id: p.memberId,
        name: p.name,
        hint: guardianHint(p),
        isTrainer: p.isAssignedTrainer,
        wantsActiveTraining: p.wantsActiveTraining,
      })),
    },
    ...(waitlist.length > 0
      ? [
          {
            key: 'waitlist',
            label: 'Warteliste',
            icon: <span className="text-amber-600">?</span>,
            countClass: 'text-amber-600',
            rowClass: 'bg-amber-50',
            items: waitlist.map((p) => ({ id: p.memberId, name: p.name, hint: guardianHint(p) })),
          },
        ]
      : []),
    {
      key: 'declined',
      label: 'Abgemeldet',
      icon: <span className="text-red-600">✕</span>,
      countClass: 'text-red-600',
      rowClass: 'bg-red-50',
      trainerRowClass: 'bg-red-200',
      items: declined.map((p) => ({ id: p.memberId, name: p.name, hint: guardianHint(p), isTrainer: p.isAssignedTrainer })),
    },
  ];

  const fullDateLabel = new Date(`${card.eventDate}T00:00:00`).toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  return (
    <TrainingMenuModal
      title={card.title}
      subtitle={`${fullDateLabel} · ${card.startTime.slice(0, 5)}–${card.endTime.slice(0, 5)}${
        card.venue ? ` · ${card.venue}` : ''
      }`}
      eventId={card.id}
      respondableMembers={card.respondableMembers}
      activeTrainingOffered={card.activeTrainingOffered}
      onRespond={(memberId, status, wantsActiveTraining) => respond.mutate({ memberId, status, wantsActiveTraining })}
      respondPending={respond.isPending}
      canRespond={can('training.respond')}
      rosterGroups={rosterGroups}
      onReset={can('training.manage') ? (memberId) => reset.mutate(memberId) : undefined}
      resettingId={reset.isPending ? (reset.variables ?? null) : null}
      shareUrl={buildTrainingEventPath(card)}
      onEdit={onEdit}
      onDelete={onDelete}
      deletePending={deletePending}
      onClose={onClose}
    />
  );
}

/** Checkbox multi-select for a series'/event's own target group(s) — distinct
 * from `GroupFilterDropdown` above, which filters the *view*, not what an
 * event targets. A session may span more than one group at once (e.g. a
 * combined JTG1+JTG2 training). */
function GroupCheckboxes({
  groups,
  selected,
  onChange,
}: {
  groups: TrainingGroupResponse[] | undefined;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  function toggle(id: string) {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  }
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1">
      {groups?.length === 0 && <p className="text-sm text-slate-400">Keine Gruppen angelegt.</p>}
      {groups?.map((g) => (
        <label key={g.id} className="flex items-center gap-1.5 text-sm">
          <input type="checkbox" checked={selected.includes(g.id)} onChange={() => toggle(g.id)} />
          {g.name}
        </label>
      ))}
    </div>
  );
}

/** Trainer-Auswahl beim Anlegen/Bearbeiten einer Serie bzw. eines
 * Einzeltermins (§ Nutzeranfrage: "Dropdown Menü Checkbox") — gleiche
 * Dropdown-Mechanik wie `GroupFilterDropdown`/`WeekdayFilterDropdown`
 * (`FilterDropdowns.tsx`), hier aber als Formularfeld (schreibt in
 * `trainerMemberIds`) statt als Listen-Filter. `trainers` kommt von
 * `GET /training/trainers` — nur Mitglieder mit `members.isTrainer`
 * (Verwaltung → Verein → Trainer), serverseitig nochmals durchgesetzt
 * (`TrainingService.assertTrainers`). */
function TrainerCheckboxDropdown({
  trainers,
  selected,
  onChange,
}: {
  trainers: TrainingTrainerOption[] | undefined;
  selected: string[];
  onChange: (ids: string[]) => void;
}) {
  function toggle(memberId: string) {
    onChange(selected.includes(memberId) ? selected.filter((x) => x !== memberId) : [...selected, memberId]);
  }
  const label =
    selected.length === 0
      ? 'Kein Trainer ausgewählt'
      : selected.length === 1
        ? (trainers?.find((t) => t.memberId === selected[0])?.name ?? '1 Trainer')
        : `${selected.length} Trainer`;

  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useClickOutside(rootRef, () => setOpen(false), open);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full cursor-pointer rounded-md border border-slate-300 px-3 py-1.5 text-left text-sm text-slate-700"
      >
        {label} ▾
      </button>
      {open && (
        <div className="absolute left-0 z-10 mt-1 w-full min-w-56 rounded-md border border-slate-200 bg-white p-2 shadow-lg">
          {trainers?.length === 0 && (
            <p className="px-2 py-1 text-sm text-slate-400">
              Keine Trainer angelegt (Verwaltung → Verein → Trainer).
            </p>
          )}
          {trainers?.map((t) => (
            <label key={t.memberId} className="flex items-center gap-2 rounded px-2 py-1 text-sm hover:bg-slate-50">
              <input type="checkbox" checked={selected.includes(t.memberId)} onChange={() => toggle(t.memberId)} />
              {t.name}
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-blue-600 hover:bg-slate-50"
              onClick={() => onChange([])}
            >
              Auswahl zurücksetzen
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Shared by "Serientermin" (in `AddEventModal`) and "Serie bearbeiten" (in
 * `ManageSeriesModal`) — identical fields, only the submit action
 * (create+generate vs. patch+regenerate) differs. */
function SeriesFormFields({
  editing,
  groups,
  trainers,
  onDone,
  onCancel,
}: {
  editing: TrainingSeriesRow | null;
  groups: TrainingGroupResponse[] | undefined;
  trainers: TrainingTrainerOption[] | undefined;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(editing?.title ?? '');
  const [groupIds, setGroupIds] = useState<string[]>(editing?.trainingGroupIds ?? []);
  const [trainerIds, setTrainerIds] = useState<string[]>(editing?.trainerMemberIds ?? []);
  const [trainerPossible, setTrainerPossible] = useState(editing?.trainerPossible ?? true);
  const [activeTrainingOffered, setActiveTrainingOffered] = useState(editing?.activeTrainingOffered ?? false);
  const [venue, setVenue] = useState(editing?.venue ?? '');
  const [days, setDays] = useState<number[]>(editing?.weekdays ?? [2]);
  const [start, setStart] = useState(editing?.startDate ?? '');
  const [end, setEnd] = useState(editing?.endDate ?? '');
  const [from, setFrom] = useState(editing?.startTime.slice(0, 5) ?? '16:00');
  const [to, setTo] = useState(editing?.endTime.slice(0, 5) ?? '17:30');
  const [result, setResult] = useState<{ created: number; updated: number } | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        title,
        trainingGroupIds: groupIds,
        // Ohne vorgesehenen Trainereinsatz ergibt eine Trainer-Zuweisung
        // keinen Sinn (§ Nutzeranfrage) — das Formular blendet die Auswahl
        // dann aus, hier zusätzlich serverseitig geleert statt eine
        // möglicherweise veraltete Auswahl stillschweigend mitzuschicken.
        trainerMemberIds: trainerPossible ? trainerIds : [],
        trainerPossible,
        activeTrainingOffered,
        venue: venue || undefined,
        weekdays: days,
        startDate: start,
        endDate: end,
        startTime: from,
        endTime: to,
      };
      let id: string;
      if (editing) {
        await api(`/training/series/${editing.id}`, { method: 'PATCH', orgScoped: true, body });
        id = editing.id;
      } else {
        const created = await api<{ id: string }>('/training/series', { method: 'POST', orgScoped: true, body });
        id = created.id;
      }
      // Editing regenerates the schedule (new/removed dates) and updates
      // already-materialized future events with the new series data (venue,
      // times, group, ...); a fresh series just materializes its first batch.
      return api<{ created: number; updated: number; skipped: number; pruned: number }>(
        `/training/series/${id}/generate`,
        { method: 'POST', orgScoped: true, body: { pruneOutside: !!editing } },
      );
    },
    onSuccess: (res) => {
      setResult({ created: res.created, updated: res.updated });
      void qc.invalidateQueries({ queryKey: ['training'] });
      if (!editing) {
        setTitle('');
        setVenue('');
      }
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <FormSection label="Was">
        <div className="col-span-2">
          <Field label="Titel">
            <Input className="px-2.5 py-1.5" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
        </div>
        <div className="col-span-2">
          <span className="mb-1 block text-sm font-medium text-slate-600">Gruppe(n)</span>
          <GroupCheckboxes groups={groups} selected={groupIds} onChange={setGroupIds} />
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={trainerPossible}
              onChange={(e) => setTrainerPossible(e.target.checked)}
            />
            Trainereinsatz an diesen Terminen möglich
          </label>
          {trainerPossible && (
            <div className="mt-2">
              <span className="mb-1 block text-sm font-medium text-slate-600">Trainer</span>
              <TrainerCheckboxDropdown trainers={trainers} selected={trainerIds} onChange={setTrainerIds} />
            </div>
          )}
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={activeTrainingOffered}
              onChange={(e) => setActiveTrainingOffered(e.target.checked)}
            />
            Option „Training" bei der Rückmeldung anbieten
          </label>
          <p className="mt-1 text-xs text-slate-400">
            Mitglieder können beim Anmelden zusätzlich angeben, dass sie aktiv trainieren möchten — in der
            Teilnehmerliste als grüne Kapsel sichtbar.
          </p>
        </div>
      </FormSection>

      <FormSection label="Wann">
        <div className="col-span-2">
          <span className="mb-1 block text-sm font-medium text-slate-600">Wochentage (mind. einer)</span>
          <div className="flex gap-1">
            {WD.map((d, i) => (
              <button
                type="button"
                key={d}
                onClick={() =>
                  setDays((cur) => (cur.includes(i + 1) ? cur.filter((x) => x !== i + 1) : [...cur, i + 1]))
                }
                className={`rounded-md px-2.5 py-1.5 text-xs font-medium transition ${
                  days.includes(i + 1) ? 'bg-brand-accent text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
          {days.length === 0 && <p className="mt-1 text-xs text-red-600">Bitte mindestens einen Wochentag wählen.</p>}
        </div>
        <Field label="Von (Datum)">
          <Input className="px-2.5 py-1.5" type="date" value={start} onChange={(e) => setStart(e.target.value)} required />
        </Field>
        <Field label="Bis (Datum)">
          <Input className="px-2.5 py-1.5" type="date" value={end} onChange={(e) => setEnd(e.target.value)} required />
        </Field>
        <Field label="Startzeit">
          <Input className="px-2.5 py-1.5" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Endzeit">
          <Input className="px-2.5 py-1.5" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </FormSection>

      <FormSection label="Wo">
        <div className="col-span-2">
          <Field label="Trainingsort">
            <Input
              className="px-2.5 py-1.5"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="z. B. Sporthalle Hülser Straße"
            />
          </Field>
        </div>
      </FormSection>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="submit" disabled={save.isPending || days.length === 0}>
          {editing ? 'Speichern & Termine aktualisieren' : 'Serie anlegen & Termine erzeugen'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
        )}
      </div>
      {save.isError && (
        <Alert>{save.error instanceof Error ? save.error.message : 'Serie konnte nicht gespeichert werden.'}</Alert>
      )}
      {result !== null && !save.isError && (
        <Alert kind="success">
          {result.created === 0 && result.updated === 0
            ? 'Gespeichert, aber 0 Termine erzeugt — Zeitraum/Wochentage prüfen.'
            : [
                result.created > 0 && `${result.created} Termin${result.created === 1 ? '' : 'e'} erzeugt`,
                result.updated > 0 &&
                  `${result.updated} bestehende${result.updated === 1 ? 'r' : 'n'} Termin${result.updated === 1 ? '' : 'e'} aktualisiert`,
              ]
                .filter(Boolean)
                .join(', ') + '.'}
        </Alert>
      )}
    </form>
  );
}

/** Standalone single event, not tied to any recurring series — the
 * "Einzeltermin" tab of `AddEventModal`, and (mit `editing` gesetzt, §
 * Nutzeranfrage) auch das "Termin bearbeiten"-Formular eines einzelnen,
 * bereits bestehenden Termins (egal ob frei stehend oder aus einer Serie
 * materialisiert — Bearbeiten betrifft dann laut `updateEvent()` immer nur
 * diesen einen Termin, die Serie selbst bleibt unverändert, siehe
 * docs/training.md). Gleiches `editing: X | null`-Muster wie
 * `SeriesFormFields`. */
function EventFormFields({
  editing,
  groups,
  trainers,
  onDone,
  onCancel,
}: {
  editing: TrainingEventRow | null;
  groups: TrainingGroupResponse[] | undefined;
  trainers: TrainingTrainerOption[] | undefined;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(editing?.title ?? '');
  const [groupIds, setGroupIds] = useState<string[]>(editing?.trainingGroupIds ?? []);
  const [trainerIds, setTrainerIds] = useState<string[]>(editing?.trainerMemberIds ?? []);
  const [trainerPossible, setTrainerPossible] = useState(editing?.trainerPossible ?? true);
  const [activeTrainingOffered, setActiveTrainingOffered] = useState(editing?.activeTrainingOffered ?? false);
  const [venue, setVenue] = useState(editing?.venue ?? '');
  const [date, setDate] = useState(editing?.eventDate ?? '');
  const [from, setFrom] = useState(editing?.startTime.slice(0, 5) ?? '16:00');
  const [to, setTo] = useState(editing?.endTime.slice(0, 5) ?? '17:30');
  const [max, setMax] = useState(editing?.maxParticipants ? String(editing.maxParticipants) : '');
  const [warningText, setWarningText] = useState(editing?.warningText ?? '');
  const [warningColor, setWarningColor] = useState(editing?.warningColor ?? WARNING_COLOR_PRESETS[0]!.hex);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        title,
        trainingGroupIds: groupIds,
        // See SeriesFormFields' submit for why trainerIds is dropped here.
        trainerMemberIds: trainerPossible ? trainerIds : [],
        trainerPossible,
        activeTrainingOffered,
        venue: venue || undefined,
        eventDate: date,
        startTime: from,
        endTime: to,
        maxParticipants: max ? Number(max) : undefined,
        warningText: warningText.trim() || undefined,
        warningColor: warningText.trim() ? warningColor : undefined,
      };
      return editing
        ? api(`/training/events/${editing.id}`, { method: 'PATCH', orgScoped: true, body })
        : api('/training/events', { method: 'POST', orgScoped: true, body });
    },
    onSuccess: () => {
      if (!editing) {
        setTitle('');
        setVenue('');
        setDate('');
        setMax('');
        setTrainerIds([]);
        setTrainerPossible(true);
        setActiveTrainingOffered(false);
        setWarningText('');
        setWarningColor(WARNING_COLOR_PRESETS[0]!.hex);
      }
      void qc.invalidateQueries({ queryKey: ['training'] });
      onDone();
    },
  });

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {editing?.seriesId && (
        <Alert>
          Dieser Termin stammt aus einer Serie — die Änderung betrifft nur diesen einen Termin, die
          übrigen Termine der Serie bleiben unverändert.
        </Alert>
      )}
      <FormSection label="Was">
        <div className="col-span-2">
          <Field label="Titel">
            <Input className="px-2.5 py-1.5" value={title} onChange={(e) => setTitle(e.target.value)} required />
          </Field>
        </div>
        <div className="col-span-2">
          <span className="mb-1 block text-sm font-medium text-slate-600">Gruppe(n)</span>
          <GroupCheckboxes groups={groups} selected={groupIds} onChange={setGroupIds} />
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={trainerPossible}
              onChange={(e) => setTrainerPossible(e.target.checked)}
            />
            Trainereinsatz an diesen Terminen möglich
          </label>
          {trainerPossible && (
            <div className="mt-2">
              <span className="mb-1 block text-sm font-medium text-slate-600">Trainer</span>
              <TrainerCheckboxDropdown trainers={trainers} selected={trainerIds} onChange={setTrainerIds} />
            </div>
          )}
        </div>
        <div className="col-span-2">
          <label className="flex items-center gap-2 text-sm font-medium text-slate-600">
            <input
              type="checkbox"
              checked={activeTrainingOffered}
              onChange={(e) => setActiveTrainingOffered(e.target.checked)}
            />
            Option „Training" bei der Rückmeldung anbieten
          </label>
          <p className="mt-1 text-xs text-slate-400">
            Mitglieder können beim Anmelden zusätzlich angeben, dass sie aktiv trainieren möchten — in der
            Teilnehmerliste als grüne Kapsel sichtbar.
          </p>
        </div>
      </FormSection>

      {/* § Nutzerfeedback „Überschneidungen, komische Anordnung" — Datum und
          Max. Teilnehmer jeweils allein auf voller Breite (`col-span-2`)
          statt nebeneinander: "Max. Teilnehmer (optional)" umbricht auf
          schmalen Mobil-Breiten auf zwei Zeilen, was die Felder einer
          gemeinsamen Grid-Zeile sonst uneinheitlich hoch macht und
          dadurch wie überlappend wirken ließ. Start-/Endzeit haben beide
          kurze einzeilige Label und bleiben deshalb als einziges Paar
          nebeneinander. */}
      <FormSection label="Wann">
        <div className="col-span-2">
          <Field label="Datum">
            <Input className="px-2.5 py-1.5" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
          </Field>
        </div>
        <Field label="Startzeit">
          <Input className="px-2.5 py-1.5" type="time" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label="Endzeit">
          <Input className="px-2.5 py-1.5" type="time" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
        <div className="col-span-2">
          <Field label="Max. Teilnehmer (optional)">
            <Input className="px-2.5 py-1.5" type="number" min={1} value={max} onChange={(e) => setMax(e.target.value)} />
          </Field>
        </div>
      </FormSection>

      <FormSection label="Wo">
        <div className="col-span-2">
          <Field label="Trainingsort">
            <Input
              className="px-2.5 py-1.5"
              value={venue}
              onChange={(e) => setVenue(e.target.value)}
              placeholder="z. B. Sporthalle Hülser Straße"
            />
          </Field>
        </div>
      </FormSection>

      {/* § Nutzeranfrage: optionaler Warnhinweis mit wählbarer heller
          Hintergrundfarbe, erscheint auf der Karte unter Uhrzeit/Ort. */}
      <FormSection label="Warnung">
        <div className="col-span-2">
          <Field label="Hinweistext (optional)">
            <Input
              className="px-2.5 py-1.5"
              value={warningText}
              onChange={(e) => setWarningText(e.target.value)}
              placeholder="z. B. Halle gesperrt wegen Bauarbeiten"
              maxLength={300}
            />
          </Field>
        </div>
        {warningText.trim() && (
          <div className="col-span-2">
            <span className="mb-1 block text-sm font-medium text-slate-600">Hintergrundfarbe</span>
            <div className="flex flex-wrap gap-2">
              {WARNING_COLOR_PRESETS.map((p) => (
                <button
                  key={p.hex}
                  type="button"
                  title={p.label}
                  aria-label={p.label}
                  aria-pressed={warningColor === p.hex}
                  onClick={() => setWarningColor(p.hex)}
                  className={`h-7 w-7 rounded-full border-2 ${
                    warningColor === p.hex ? 'border-brand-accent' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: p.hex }}
                />
              ))}
            </div>
            <div
              className="mt-2 rounded-md px-2 py-1 text-xs text-slate-700"
              style={{ backgroundColor: warningColor }}
            >
              {warningText}
            </div>
          </div>
        )}
      </FormSection>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <Button type="submit" disabled={save.isPending}>
          {editing ? 'Speichern' : 'Termin anlegen'}
        </Button>
        {onCancel && (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Abbrechen
          </Button>
        )}
      </div>
      {save.isError && (
        <Alert>{save.error instanceof Error ? save.error.message : 'Termin konnte nicht gespeichert werden.'}</Alert>
      )}
    </form>
  );
}

/** Admin table of existing series — edit switches `ManageSeriesModal` to the
 * pre-filled edit form, delete removes the series and its future
 * non-overridden events. */
function SeriesListContent({ onEdit }: { onEdit: (row: TrainingSeriesRow) => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { data: series } = useQuery({
    queryKey: ['training', 'series'],
    queryFn: () => api<TrainingSeriesRow[]>('/training/series', { orgScoped: true }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/training/series/${id}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['training'] }),
  });

  return (
    <div>
      <p className="mb-3 text-xs text-slate-500">
        {series?.length ?? 0} Serie{series?.length === 1 ? '' : 'n'}
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500">
            <tr>
              <th className="py-1 pr-3">Titel</th>
              <th className="py-1 pr-3">Ort</th>
              <th className="py-1 pr-3">Wochentage</th>
              <th className="py-1 pr-3">Zeitraum</th>
              <th className="py-1 pr-3">Uhrzeit</th>
              <th className="py-1 pr-3">Status</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {series?.map((s) => (
              <tr key={s.id}>
                <td className="py-1.5 pr-3 font-medium">{s.title}</td>
                <td className="py-1.5 pr-3 text-slate-500">{s.venue ?? '—'}</td>
                <td className="py-1.5 pr-3 text-slate-500">{s.weekdays.map((d) => WD[d - 1]).join(', ')}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">
                  {s.startDate}
                  {s.endDate ? ` – ${s.endDate}` : ''}
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-slate-500">
                  {s.startTime.slice(0, 5)}–{s.endTime.slice(0, 5)}
                </td>
                <td className="py-1.5 pr-3 text-slate-500">{s.status}</td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  {can('training.edit') && (
                    <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => onEdit(s)}>
                      Bearbeiten
                    </Button>
                  )}
                  {can('training.delete') && (
                    <Button
                      variant="danger"
                      className="px-2 py-1 text-xs"
                      disabled={del.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            `Serie „${s.title}" wirklich löschen? Zukünftige, nicht bearbeitete Termine werden mitgelöscht.`,
                          )
                        ) {
                          del.mutate(s.id);
                        }
                      }}
                    >
                      Löschen
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {series?.length === 0 && (
              <tr>
                <td colSpan={7} className="py-2 text-slate-500">
                  Keine Serien.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {del.isError && <Alert>Serie konnte nicht gelöscht werden.</Alert>}
    </div>
  );
}

/** Segmented tab button for `AddEventModal` ("Einzeltermin" vs. "Serientermin"). */
function AddModeTab({
  active,
  icon,
  title,
  hint,
  onClick,
}: {
  active: boolean;
  icon: ReactNode;
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-start gap-2.5 rounded-xl border-2 px-3.5 py-3 text-left transition ${
        active
          ? 'border-brand-accent bg-brand-accent/5'
          : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
    >
      <span className={`mt-0.5 shrink-0 ${active ? 'text-brand-accent' : 'text-slate-400'}`}>{icon}</span>
      <span>
        <span className={`block text-sm font-semibold ${active ? 'text-brand-accent' : 'text-slate-700'}`}>
          {title}
        </span>
        <span className="block text-xs text-slate-500">{hint}</span>
      </span>
    </button>
  );
}

/** "Termin hinzufügen" — a single entry point for both a one-off event and a
 * recurring series, switched via tabs instead of two separate buttons on the
 * page itself (§ Nutzeranfrage: nur noch ein Button + Zahnrad-Button oben). */
function AddEventModal({
  groups,
  trainers,
  onClose,
}: {
  groups: TrainingGroupResponse[] | undefined;
  trainers: TrainingTrainerOption[] | undefined;
  onClose: () => void;
}) {
  const [mode, setMode] = useState<'event' | 'series'>('event');

  return (
    <Modal
      title="Termin hinzufügen"
      subtitle="Einzelnen Termin oder wiederkehrende Serie anlegen"
      icon={<CalendarPlus size={18} />}
      onClose={onClose}
      maxWidthClassName="max-w-xl"
    >
      <div className="mb-4 grid grid-cols-2 gap-2">
        <AddModeTab
          active={mode === 'event'}
          icon={<CalendarPlus size={18} />}
          title="Einzeltermin"
          hint="Ein einmaliger Termin"
          onClick={() => setMode('event')}
        />
        <AddModeTab
          active={mode === 'series'}
          icon={<Repeat size={18} />}
          title="Serientermin"
          hint="Wiederkehrend, z. B. wöchentlich"
          onClick={() => setMode('series')}
        />
      </div>
      {mode === 'event' ? (
        <EventFormFields editing={null} groups={groups} trainers={trainers} onDone={onClose} onCancel={onClose} />
      ) : (
        <SeriesFormFields editing={null} groups={groups} trainers={trainers} onDone={onClose} onCancel={onClose} />
      )}
    </Modal>
  );
}

/** Geöffnet über den Bearbeiten-Button im Trainings-Menü (§ Nutzeranfrage)
 * — bearbeitet immer nur diesen einen Termin (siehe `EventFormFields`'
 * `editing`-Hinweis bei Serienterminen). Exportiert, da inzwischen auch
 * `TrainingEventPage.tsx`s eigene Trainings-Seite diesen Button anbietet
 * (siehe docs/training.md) — `EventFormFields` holt/invalidiert ihre Daten
 * selbst über `useQueryClient()`, daher ohne Anpassung von dort aus
 * wiederverwendbar. */
export function EditEventModal({
  event,
  groups,
  trainers,
  onClose,
}: {
  event: TrainingEventRow;
  groups: TrainingGroupResponse[] | undefined;
  trainers: TrainingTrainerOption[] | undefined;
  onClose: () => void;
}) {
  return (
    <Modal
      title={`Termin bearbeiten — ${event.title}`}
      icon={<Pencil size={18} />}
      onClose={onClose}
      maxWidthClassName="max-w-xl"
    >
      <EventFormFields editing={event} groups={groups} trainers={trainers} onDone={onClose} onCancel={onClose} />
    </Modal>
  );
}

/** Zahnrad-Button neben "Termin hinzufügen" — bestehende Serien anzeigen und
 * bearbeiten, in einem eigenen Fenster statt eines dritten Toggle-Buttons. */
function ManageSeriesModal({
  groups,
  trainers,
  onClose,
}: {
  groups: TrainingGroupResponse[] | undefined;
  trainers: TrainingTrainerOption[] | undefined;
  onClose: () => void;
}) {
  const [editing, setEditing] = useState<TrainingSeriesRow | null>(null);

  return (
    <Modal
      title={editing ? `Serie bearbeiten — ${editing.title}` : 'Bestehende Serien'}
      subtitle={editing ? undefined : 'Trainingsserien anzeigen, bearbeiten oder löschen'}
      icon={<Settings size={18} />}
      onClose={onClose}
      maxWidthClassName={editing ? 'max-w-xl' : 'max-w-3xl'}
    >
      {editing ? (
        <SeriesFormFields
          editing={editing}
          groups={groups}
          trainers={trainers}
          onDone={() => setEditing(null)}
          onCancel={() => setEditing(null)}
        />
      ) : (
        <SeriesListContent onEdit={setEditing} />
      )}
    </Modal>
  );
}

export function TrainingPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const [groupFilter, setGroupFilter] = useState<string[]>([]);
  const [weekdayFilter, setWeekdayFilter] = useState<number[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showManage, setShowManage] = useState(false);

  const { data: events } = useQuery({
    queryKey: ['training', 'events', { groupFilter }],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '200' });
      if (groupFilter.length > 0) params.set('trainingGroupIds', groupFilter.join(','));
      return api<{ data: TrainingEventCard[] }>(`/training/events?${params}`, { orgScoped: true });
    },
  });
  // Wochentag ist rein aus `eventDate` abgeleitet, keine gespeicherte
  // Eigenschaft — Filterung passiert deshalb clientseitig über die ohnehin
  // geladene Liste, statt einen eigenen Query-Parameter einzuführen.
  const filteredEvents = events?.data.filter(
    (e) => weekdayFilter.length === 0 || weekdayFilter.includes(isoWeekday(e.eventDate)),
  );
  // Nur Wochentage anbieten, an denen tatsächlich Trainings stattfinden (§
  // Nutzeranfrage) — aus der (ggf. bereits nach Gruppe gefilterten) Liste
  // berechnet, unabhängig vom aktuellen Wochentagfilter selbst, damit sich
  // die Optionsliste beim Filtern nicht unter der Auswahl wegbewegt. Gleiches
  // Muster wie `availableSessionWeekdays` in `OrgTrainersPage.tsx`.
  const availableWeekdays = [...new Set((events?.data ?? []).map((e) => isoWeekday(e.eventDate)))].sort();
  // Every member sees this (not just admins) — it drives both the admin
  // modals' group select and the group filter dropdown below.
  const { data: groups } = useQuery({
    queryKey: ['training', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
  });
  // Nur für die Admin-Formulare (Serie/Termin anlegen/bearbeiten) nötig — anders
  // als `groups` nicht auch für einen Mitglieder-Filter gebraucht, deshalb erst
  // geladen, wenn eins der beiden Modals offen ist. Das Bearbeiten eines
  // einzelnen Termins (`EditEventModal`) läuft seit § Nutzerfeedback
  // „Adresszeile … den entsprechenden Link haben" nicht mehr hier, sondern
  // auf `TrainingEventPage.tsx`s eigener Seite — dort lädt dieselbe Liste
  // unabhängig nach, siehe docs/training.md.
  const { data: trainers } = useQuery({
    queryKey: ['training', 'trainers'],
    queryFn: () => api<TrainingTrainerOption[]>('/training/trainers', { orgScoped: true }),
    enabled: showAdd || showManage,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['training'] });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="hidden text-xl font-semibold md:block">Training</h1>
        {/* Die äußere Zeile (Titel + Filtergruppe) bleibt `flex-wrap` (§
            Nutzerfeedback "Inhalt wird außerhalb des Content-Bereichs
            dargestellt", siehe SeasonPage.tsx) — stellt auf schmalen
            Mobil-Viewports den Titel über statt neben die Filtergruppe.
            Die Filtergruppe selbst ist dagegen bewusst `flex-nowrap` (§
            Nutzerfeedback "sollen alle in eine Zeile und nicht in die
            nächste verrutschen"): die beiden Dropdown-Buttons dürfen
            schrumpfen (`min-w-0 truncate` in FilterDropdowns.tsx) und
            geben so automatisch Platz an die beiden quadratischen
            Icon-Buttons ab (`shrink-0`, feste Größe) — alle vier bleiben
            dadurch garantiert in einer Zeile, auch auf sehr schmalen
            Geräten. */}
        <div className="flex min-w-0 flex-nowrap items-center gap-2">
          <GroupFilterDropdown groups={groups ?? []} selected={groupFilter} onChange={setGroupFilter} />
          <WeekdayFilterDropdown
            selected={weekdayFilter}
            onChange={setWeekdayFilter}
            availableDays={availableWeekdays}
          />
          {can('training.create') && (
            <>
              <Button
                onClick={() => setShowAdd(true)}
                title="Termin hinzufügen"
                aria-label="Termin hinzufügen"
                className="h-9 w-9 shrink-0 px-0 py-0"
              >
                <CalendarPlus size={16} />
              </Button>
              <button
                type="button"
                title="Serien verwalten"
                aria-label="Bestehende Serien anzeigen und bearbeiten"
                onClick={() => setShowManage(true)}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-300 text-slate-600 hover:bg-slate-100"
              >
                <Settings size={16} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-3">
        {filteredEvents?.map((e) => (
          <EventCard key={e.id} e={e} />
        ))}
        {events?.data.length === 0 && (
          <p className="text-sm text-slate-500">Keine anstehenden Trainingstermine.</p>
        )}
        {events && events.data.length > 0 && filteredEvents?.length === 0 && (
          <p className="text-sm text-slate-500">
            Keine Trainingstermine an den ausgewählten Wochentagen.{' '}
            <button type="button" className="text-brand-accent hover:underline" onClick={() => setWeekdayFilter([])}>
              Filter zurücksetzen
            </button>
          </p>
        )}
      </div>

      {showAdd && (
        <AddEventModal
          groups={groups}
          trainers={trainers}
          onClose={() => {
            setShowAdd(false);
            invalidate();
          }}
        />
      )}
      {showManage && (
        <ManageSeriesModal
          groups={groups}
          trainers={trainers}
          onClose={() => {
            setShowManage(false);
            invalidate();
          }}
        />
      )}
    </div>
  );
}
